import ServerServices from "@/components/ServerServices";
import { useEffect, useState } from "react";
import {
  Play, Square, RotateCw, Settings2, ScrollText, Server, Loader2, Cpu, Hash, ExternalLink, Activity,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const COMMANDS = ["npm start", "npm run start", "node server.js", "node index.js", "node app.js"];

function ConfigDialog({ site, open, setOpen, onDone }) {
  const [command, setCommand] = useState(site?.command || "npm start");
  const [autostart, setAutostart] = useState(site?.autostart || false);
  const [autorestart, setAutorestart] = useState(site?.autorestart ?? true);
  const [envText, setEnvText] = useState(
    Object.entries(site?.env || {}).map(([k, v]) => `${k}=${v}`).join("\n")
  );

  useEffect(() => {
    if (site) {
      setCommand(site.command || "npm start");
      setAutostart(site.autostart || false);
      setAutorestart(site.autorestart ?? true);
      setEnvText(Object.entries(site.env || {}).map(([k, v]) => `${k}=${v}`).join("\n"));
    }
  }, [site]);

  const save = async () => {
    const env = {};
    envText.split("\n").forEach((l) => {
      const i = l.indexOf("=");
      if (i > 0) env[l.slice(0, i).trim()] = l.slice(i + 1).trim();
    });
    try {
      await api.patch(`/sites/${site.slug}`, { command, autostart, autorestart, env });
      toast.success("Configuration saved");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  if (!site) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0c0c0e] border-zinc-800" data-testid="config-dialog">
        <DialogHeader><DialogTitle className="font-head">Configure · {site.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Start Command</label>
            <Select value={command} onValueChange={setCommand}>
              <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-800" data-testid="command-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0c0c0e] border-zinc-800">
                {COMMANDS.map((c) => <SelectItem key={c} value={c} className="font-mono">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Custom Env (KEY=value per line)</label>
            <textarea value={envText} onChange={(e) => setEnvText(e.target.value)} rows={4} data-testid="env-textarea"
              className="mt-1.5 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-mono text-zinc-200 focus:outline-none focus:ring-1 focus:ring-zinc-600" />
            <p className="text-xs text-zinc-600 mt-1 font-mono">Protected: PORT, HOST, NODE_ENV, CANNON_SITE_* (injected automatically)</p>
          </div>
          <div className="flex items-center justify-between rounded-md border border-zinc-800 px-3 py-2.5">
            <span className="text-sm text-zinc-300">Auto-start on boot</span>
            <Switch checked={autostart} onCheckedChange={setAutostart} data-testid="autostart-switch" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-zinc-800 px-3 py-2.5">
            <span className="text-sm text-zinc-300">Auto-restart on crash (30s backoff cap)</span>
            <Switch checked={autorestart} onCheckedChange={setAutorestart} data-testid="autorestart-switch" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={save} data-testid="config-save-btn" className="rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogsDialog({ slug, open, setOpen }) {
  const [logs, setLogs] = useState("");
  useEffect(() => {
    if (open && slug) {
      const load = () => api.get(`/apps/${slug}/logs`).then(setLogs).catch(() => {});
      load();
      const t = setInterval(load, 3000);
      return () => clearInterval(t);
    }
  }, [open, slug]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0c0c0e] border-zinc-800 max-w-3xl" data-testid="applogs-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><ScrollText className="h-4 w-4" /> {slug} · logs</DialogTitle></DialogHeader>
        <pre className="max-h-[60vh] overflow-auto rounded-md border border-zinc-800 bg-black p-3 text-xs font-mono text-zinc-400 whitespace-pre-wrap">
          {logs || "No output yet."}
        </pre>
      </DialogContent>
    </Dialog>
  );
}

export default function Apps() {
  const [apps, setApps] = useState(null);
  const [config, setConfig] = useState(null);
  const [logsFor, setLogsFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const { canWrite } = useAuth();

  const load = () => api.get("/sites").then((s) => setApps(s.filter((x) => x.type === "node"))).catch(() => setApps([]));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const ctrl = async (slug, action) => {
    setBusy(`${slug}-${action}`);
    try { await api.post(`/apps/${slug}/${action}`); toast.success(`${action} · ${slug}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const [live, setLive] = useState({});
  const testLive = async (slug) => {
    setBusy(`${slug}-live`);
    try {
      const r = await api.get(`/sites/${slug}/livecheck`);
      setLive((p) => ({ ...p, [slug]: r }));
      if (r.ok) toast.success(`${slug}: HTTP ${r.status} in ${r.elapsed_ms}ms`);
      else toast.error(`${slug}: ${r.status || r.error || "unreachable"}`);
    } catch (e) { toast.error("Live check failed"); }
    finally { setBusy(null); }
  };

  if (!apps) return <Loading label="Loading apps" />;

  return (
    <div data-testid="apps-page">
      <PageHeader title="Apps" breadcrumb="cannon / apps" subtitle={`${apps.length} Node.js process${apps.length === 1 ? "" : "es"} · ports 12000–12999`} />
      <ConfigDialog site={config} open={!!config} setOpen={(o) => !o && setConfig(null)} onDone={load} />
      <LogsDialog slug={logsFor} open={!!logsFor} setOpen={(o) => !o && setLogsFor(null)} />

      {apps.length === 0 ? (
        <EmptyState icon={Server} title="No Node apps">Deploy a ZIP with a package.json to run a Node app.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {apps.map((a) => {
            const p = a.process || {};
            return (
              <div key={a.slug} className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4" data-testid={`app-card-${a.slug}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-violet-400" />
                    <div>
                      <div className="text-zinc-100">{a.name}</div>
                      <div className="font-mono text-xs text-zinc-500">{a.slug}.studyformath.site</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <StatusBadge status={p.status || "STOPPED"} />
                    {p.status === "RUNNING" && p.healthy != null && (
                      <span data-testid={`health-${a.slug}`} title={p.healthy ? "Port responding" : "Port not responding"}
                        className={cn("ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase", p.healthy ? "text-emerald-400" : "text-rose-400")}>
                        <span className={cn("h-1.5 w-1.5 rounded-full", p.healthy ? "bg-emerald-400" : "bg-rose-400 animate-pulse")} />
                        {p.healthy ? "healthy" : "unhealthy"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4 font-mono text-xs">
                  <div><div className="text-zinc-600 uppercase tracking-wide flex items-center gap-1"><Hash className="h-3 w-3" />PID</div><div className="text-zinc-300 mt-0.5">{p.pid || "—"}</div></div>
                  <div><div className="text-zinc-600 uppercase tracking-wide flex items-center gap-1"><Cpu className="h-3 w-3" />Port</div><div className="text-zinc-300 mt-0.5">{p.port || "—"}</div></div>
                  <div><div className="text-zinc-600 uppercase tracking-wide flex items-center gap-1"><RotateCw className="h-3 w-3" />Restarts</div><div className="text-zinc-300 mt-0.5">{p.restarts || 0}</div></div>
                </div>

                <div className="flex items-center gap-1.5 mt-4 pt-4 border-t border-zinc-800">
                  {canWrite && (
                    <div className="flex rounded-md border border-zinc-800 overflow-hidden">
                      <button onClick={() => ctrl(a.slug, "start")} disabled={!!busy} data-testid={`start-${a.slug}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40">
                        {busy === `${a.slug}-start` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Start
                      </button>
                      <button onClick={() => ctrl(a.slug, "restart")} disabled={!!busy} data-testid={`restart-${a.slug}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-amber-400 hover:bg-amber-500/10 border-l border-zinc-800 disabled:opacity-40">
                        <RotateCw className="h-3.5 w-3.5" /> Restart
                      </button>
                      <button onClick={() => ctrl(a.slug, "stop")} disabled={!!busy} data-testid={`stop-${a.slug}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-rose-400 hover:bg-rose-500/10 border-l border-zinc-800 disabled:opacity-40">
                        <Square className="h-3.5 w-3.5" /> Stop
                      </button>
                    </div>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => testLive(a.slug)} disabled={busy === `${a.slug}-live`} data-testid={`livecheck-${a.slug}`}
                      title="Fetch the real hostname and show HTTP status"
                      className="flex items-center gap-1 rounded border border-zinc-800 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">
                      {busy === `${a.slug}-live` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />} Test Live
                      {live[a.slug] && <span className={cn("ml-1 font-mono", live[a.slug].ok ? "text-emerald-400" : "text-rose-400")}>{live[a.slug].status || live[a.slug].error || "ERR"}</span>}
                    </button>
                    {p.status === "RUNNING" && (
                      <a href={`https://${a.slug}.studyformath.site`} target="_blank" rel="noopener noreferrer"
                        data-testid={`preview-${a.slug}`} title="Open live site"
                        className="flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 px-2 py-1 text-xs hover:bg-emerald-500/10">
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </a>
                    )}
                    {canWrite && <button onClick={() => setConfig(a)} data-testid={`config-${a.slug}`} className="p-1.5 text-zinc-400 hover:text-zinc-100 rounded hover:bg-zinc-800"><Settings2 className="h-4 w-4" /></button>}
                    <button onClick={() => setLogsFor(a.slug)} data-testid={`logs-${a.slug}`} className="p-1.5 text-zinc-400 hover:text-zinc-100 rounded hover:bg-zinc-800"><ScrollText className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ServerServices />
    </div>
  );
}
